import { gsap } from "gsap"
import settings from "@/scripts/common/settings"
import { ScrollToPlugin } from "gsap/ScrollToPlugin"
import utilities from "@/scripts/common/utilities"
import { LazyLoad } from '@/scripts/lazyload/Lazyload'
import { addScrollController } from "@/scripts/common/api/scrolltrigger.services"
import { ScrollTrigger } from "gsap/ScrollTrigger"

gsap.registerPlugin(ScrollTrigger)
gsap.registerPlugin(ScrollToPlugin)

export const Loader = (() => {
    class InfiniteLoader {
        constructor(container, options) {
            this.$loader = container
            this.$addedItem = null

            this.options = Object.assign(defaultLoaderOptions, options)

            this.options.skeletonLoaderStyle = this.$loader.dataset.skeletonType || this.options.skeletonLoaderStyle
            this.options.skeletonLoaderNo = this.$loader.dataset.skeletonLoaderNo || this.options.skeletonLoaderNo

            this.scrollOptions = {
                trigger: this.$loader.dataset.trigger || this.$loader,
                endTrigger: this.$loader.dataset.endTrigger || this.$loader,
                start: 'top top',
                end: 'bottom bottom',
                pinSpacing: this.isMobile ? false : true
            }

            this.scrollController = null

            this.events = []
            this.init()
        }

        init() {
            this.eventsCreator()
            this.updateOptions()
            this.addMainSCrollController()
            this.addListeners()
            this.$loader.getLoader = () => this
        }

        // Skeleton Loader
        skeletonLoader() {
            const loader = document.createElement('div')
            loader.classList.add('skeleton-item')
            const skeleton = document.createElement('div')
            skeleton.classList.add('skeleton__loader')

            switch (this.options.skeletonLoaderStyle) {
                case 'article-blog':
                    skeleton.classList.add('skeleton__loader--article-blog')
                    skeleton.innerHTML = `<div class="skeleton__mask">
                        <div class="skeleton__mask-a"></div>
                        <div class="skeleton__mask-b"></div>
                        <div class="skeleton__mask-c"></div>
                        <div class="skeleton__mask-d"></div>
                        <div class="skeleton__mask-e"></div>
                        <div class="skeleton__mask-f"></div>
                        <div class="skeleton__mask-g"></div>
                        <div class="skeleton__mask-h"></div>
                        <div class="skeleton__mask-i"></div>
                        <div class="skeleton__mask-j"></div>
                        <div class="skeleton__mask-k"></div>
                        <div class="skeleton__mask-l"></div>
                        <div class="skeleton__mask-m"></div>
                    </div>`
                    break
                case 'article-clean':
                    skeleton.classList.add('skeleton__loader--article-clean')
                    skeleton.innerHTML = `<div class="skeleton__mask">
                            <div class="skeleton__mask-a"></div>
                            <div class="skeleton__mask-b"></div>
                            <div class="skeleton__mask-c"></div>
                            <div class="skeleton__mask-d"></div>
                            <div class="skeleton__mask-e"></div>
                            <div class="skeleton__mask-f"></div>
                            <div class="skeleton__mask-g"></div>
                            <div class="skeleton__mask-h"></div>
                            <div class="skeleton__mask-i"></div>
                            <div class="skeleton__mask-j"></div>
                        </div>`
                    break
                case 'figure':
                    skeleton.classList.add('skeleton__loader--figure')
                    skeleton.innerHTML = `<div class="skeleton__mask">
                        <div class="skeleton__mask-a"></div>
                        <div class="skeleton__mask-b"></div>
                        <div class="skeleton__mask-c"></div>
                        <div class="skeleton__mask-d"></div>
                    </div>`
                    break
                default:
                    skeleton.innerHTML = `<div class="skeleton__mask"> </div>`
            }

            loader.appendChild(skeleton)

            if (this.options.skeletonLoaderClass) {
                const classes = this.options.skeletonLoaderClass.split(' ')
                classes.forEach(c => loader.classList.add(c))
            }


            return loader
        }

        // Refresh Pin
        refresh() {
            if (this.scrollController) this.scrollController.refresh()
            this.$loader.dispatchEvent(this.events['onUpdate'])
        }

        // Scroll to listing func

        scrollToListing() {
            gsap.to(window, {
                duration: 0.3,
                scrollTo: this.$loader
            })
        }

        // Callbacks
        updateOptions() {
            Object.assign(this.scrollOptions, {
                onLeave: () => {
                    this.$loader.dispatchEvent(this.events['onScrollEnd'])
                }
            })
        }

        addSkeletonLoader() {
            let n = 0
            while (n < this.options.skeletonLoaderNo) {
                this.addItem(this.skeletonLoader())
                n++
            }
        }

        removeSkeletonLoader() {
            const $loaders = this.$loader.querySelectorAll('.skeleton-item')
            this.clearAllItems($loaders)
        }

        addItem(domStr) {
            const $item = typeof domStr === "string" ? utilities.createNodeParsing(domStr) : domStr
            this.$addedItem = $item
            this.$loader.appendChild($item)
            const $lazyload = $item.querySelector('.lazyload')
            if ($lazyload) new LazyLoad($lazyload)

            this.$loader.dispatchEvent(this.events['onAdd'])
        }

        clearAllItems($loaders) {
            const $listItems = $loaders || gsap.utils.toArray(this.$loader.children)
            $listItems.forEach($item => {
                if(!!!$item.dataset.persistent) this.$loader.removeChild($item)
            })
            this.$loader.dispatchEvent(this.events['onClear'])
        }

        noItemsFound(show) {
            const infoDiv = this.$loader.querySelector(`.info-message`)
            if(infoDiv) infoDiv.remove()
            
            if(show) {
                const infoDiv = document.createElement('div')
                infoDiv.classList.add(`info-message`)
                infoDiv.innerHTML = `<p class="">${this.options.noItemMessage}</p>`
                this.$loader.prepend(infoDiv)
                return
            }
        }

        // Events definer
        eventsCreator() {

            this.events['onAdd'] = new CustomEvent('onAdd', {
                bubbles: true,
                detail: {
                    getAddedItem: () => {
                        return this.$addedItem
                    }
                }
            })

            this.events['onClear'] = new CustomEvent('onClear', {
                bubbles: true,
            })

            this.events['onScrollEnd'] = new CustomEvent('onScrollEnd', {
                bubbles: true,
            })

            this.events['onUpdate'] = new CustomEvent('onUpdate', {
                bubbles: true,
            })
        }

        // Resize func
        resizeFunc() {
            return utilities.debounce(() => {
                this.refresh()
            }, 250)
        }

        // Adding scrollController
        addMainSCrollController() {
            if (this.scrollController) this.scrollController = null

            this.scrollController = ScrollTrigger.create(this.scrollOptions)
            addScrollController(this.scrollController)
        }

        // Adding Listeners
        addListeners() {
            this.$loader.addEventListener('onAdd', () => this.refresh())
            this.$loader.addEventListener('onClear', () => this.refresh())
            window.addEventListener('resize', this.resizeFunc())
        }
    }

    const defaultLoaderOptions = {
        skeletonLoaderStyle: 'article-blog',
        skeletonLoaderNo: 3,
        noItemMessage: 'We are sorry. No result were found for the selected filters(s)'
    }

    return {
        InfiniteLoader
    }
})()